import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { MasterEntry, AdminRole } from '../types';
import { Badge, Button, Input, Search, Tooltip } from '../../components/core';

interface MastersManagementProps {
  masters: MasterEntry[];
  currentRole: AdminRole;
  onAddMaster: (master: MasterEntry) => void;
  onUpdateMaster?: (master: MasterEntry) => void;
  onDeleteMaster?: (id: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const MastersManagement: React.FC<MastersManagementProps> = ({
  masters,
  currentRole,
  onAddMaster,
  onUpdateMaster,
  onDeleteMaster,
  addToast,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState<MasterEntry | null>(null);

  const [formData, setFormData] = useState({
    category: 'Sects' as MasterEntry['category'],
    value: '',
    code: '',
    sortOrder: 1,
    description: '',
    status: 'Active' as 'Active' | 'Inactive',
  });

  const categories = ['Sects', 'Cities', 'Venues', 'Qualifications', 'Relationships', 'Documents'];

  const filteredMasters = masters.filter((m) => {
    const matchesCategory = selectedCategory === 'ALL' || m.category === selectedCategory;
    const matchesSearch =
      m.value.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.code && m.code.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handleOpenAdd = () => {
    setEditingMaster(null);
    setFormData({
      category: 'Sects',
      value: '',
      code: `MST-${Date.now().toString().slice(-4)}`,
      sortOrder: masters.length + 1,
      description: '',
      status: 'Active',
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (item: MasterEntry) => {
    setEditingMaster(item);
    setFormData({
      category: item.category,
      value: item.value,
      code: item.code || item.id,
      sortOrder: item.sortOrder || 1,
      description: item.description || '',
      status: item.status,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.value.trim()) {
      addToast('error', 'Required Field', 'Item value / display name is required.');
      return;
    }

    if (editingMaster) {
      if (onUpdateMaster) {
        onUpdateMaster({ ...editingMaster, ...formData });
      }
      addToast('success', 'Master Updated', `Updated ${formData.value}`);
    } else {
      const newItem: MasterEntry = {
        id: `MST-${Date.now()}`,
        ...formData,
      };
      onAddMaster(newItem);
      addToast('success', 'Lookup Added', `Added ${formData.value} to ${formData.category}`);
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="border-l-4 border-[var(--gv-brand)] pl-3.5">
          <h1 className="text-2xl font-black text-[var(--gv-ink)] tracking-tight">Masters & Lookups Configurator</h1>
        </div>

        <Button leftIcon={Plus} onClick={handleOpenAdd} className="self-start sm:self-auto">
          + Add Lookup Value
        </Button>
      </div>

      {/* Filter & Search Bar */}
      <div className="admin-card flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <button
            type="button"
            onClick={() => setSelectedCategory('ALL')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              selectedCategory === 'ALL' ? 'admin-tab-active' : 'admin-tab-idle'
            }`}
          >
            All Masters ({masters.length})
          </button>
          {categories.map((cat) => {
            const count = masters.filter((m) => m.category === cat).length;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === cat ? 'admin-tab-active' : 'admin-tab-idle'
                }`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        <Search
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search lookup items..."
          containerClassName="w-full sm:w-64"
        />
      </div>

      {/* Master Data Table */}
      <div className="admin-card overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-stone-50 border-b border-[var(--gv-border)] text-[10px] font-bold text-[var(--gv-ink-muted)] uppercase tracking-wider font-mono">
            <tr>
              <th className="py-3.5 px-4">CATEGORY</th>
              <th className="py-3.5 px-4">CODE</th>
              <th className="py-3.5 px-4">DISPLAY NAME / VALUE</th>
              <th className="py-3.5 px-4">DESCRIPTION</th>
              <th className="py-3.5 px-4">STATUS</th>
              <th className="py-3.5 px-4 text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 font-medium text-[var(--gv-ink-muted)]">
            {filteredMasters.map((item) => (
              <tr key={item.id} className="hover:bg-stone-50/80 transition-colors">
                <td className="py-3.5 px-4">
                  <Badge variant="outline" className="font-mono normal-case">
                    {item.category}
                  </Badge>
                </td>
                <td className="py-3.5 px-4 font-mono text-[var(--gv-ink-muted)]">{item.code || item.id}</td>
                <td className="py-3.5 px-4 font-bold text-[var(--gv-ink)]">{item.value}</td>
                <td className="py-3.5 px-4 text-[var(--gv-ink-muted)]">{item.description || 'System master item'}</td>
                <td className="py-3.5 px-4">
                  <Badge variant={item.status === 'Active' ? 'success' : 'outline'}>
                    {item.status}
                  </Badge>
                </td>
                <td className="py-3.5 px-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Tooltip content="Edit lookup item">
                      <Button
                        variant="outline"
                        size="icon"
                        leftIcon={Edit2}
                        aria-label={`Edit ${item.value}`}
                        onClick={() => handleOpenEdit(item)}
                      />
                    </Tooltip>
                    {onDeleteMaster && (
                      <Tooltip content="Delete lookup item">
                        <Button
                          variant="outline"
                          size="icon"
                          leftIcon={Trash2}
                          aria-label={`Delete ${item.value}`}
                          className="border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                          onClick={() => {
                            if (confirm('Delete master entry?')) {
                              onDeleteMaster(item.id);
                              addToast('info', 'Deleted', 'Lookup item removed.');
                            }
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Popup Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card w-full max-w-md p-6 shadow-2xl relative space-y-4">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-black text-[var(--gv-ink)]">
                {editingMaster ? 'Edit Lookup Item' : 'Add Master Lookup Value'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsModalOpen(false)}
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Master Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="Display Value / Name"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="e.g. Shvetambar Murtipujak or Borivali West"
                required
              />

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Item Code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                />
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Description / Notes</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-2.5 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  placeholder="Optional details..."
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--gv-border)]">
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingMaster ? 'Save Changes' : 'Create Entry'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
